import {
  Button,
  Divider,
  Stack,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { BUTTON_TEXT } from 'portal/src/constants/button-text';

export type ExtraButtonConfig = {
  onClick: () => void;
  label: string;
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  isDisabled?: boolean;
  tooltipMessage?: string;
  variant?: 'contained' | 'outlined' | 'text';
};

export type FormButtonLayout = 'side' | 'bottom';

const FormButtonColumn = ({
  backButtonlabel,
  buttonLayout = 'side',
  extraButtons,
  hideCancelButton,
  isDirty,
  isSubmitDisabled,
  isNew,
  isPending,
  isReadOnly,
  saveOrUpdateLabel,
  handleBackClick,
  handleCancelClick,
  handleSubmit,
}: {
  backButtonlabel: string;
  buttonLayout?: FormButtonLayout;
  extraButtons?: ExtraButtonConfig[];
  hideCancelButton?: boolean;
  isDirty: boolean;
  isSubmitDisabled?: boolean;
  isNew: boolean;
  isPending: boolean;
  isReadOnly: boolean;
  saveOrUpdateLabel?: string;
  handleBackClick?: () => void;
  handleCancelClick: () => void;
  handleSubmit: (data: any) => void;
}) => {
  const theme = useTheme();
  const isTabletOrBelow = useMediaQuery(theme.breakpoints.down('lg'));

  const TooltipWrapper = ({
    children,
    tooltipMessage,
  }: {
    children: React.ReactNode;
    tooltipMessage?: string;
  }) =>
    tooltipMessage ? (
      <Tooltip title={tooltipMessage}>
        <span style={{ width: '100%' }}>{children}</span>
      </Tooltip>
    ) : (
      <>{children}</>
    );

  const showPrimaryButton = !isReadOnly;
  const showCancelButton = isDirty && !hideCancelButton;
  const showBackButton = !isDirty && !!handleBackClick;
  const showExtraButtons = buttonLayout === 'side' && !!extraButtons;
  const isBottomLayout = buttonLayout === 'bottom';
  const primaryButtonLabel =
    saveOrUpdateLabel ?? (isNew ? BUTTON_TEXT.save : BUTTON_TEXT.update);

  return (
    <Stack
      direction={isBottomLayout ? 'row' : 'column'}
      spacing={2}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: isBottomLayout ? 'flex-end' : 'flex-start',
        alignSelf: isBottomLayout ? 'none' : 'flex-start',
        position: isBottomLayout ? 'static' : 'sticky',
        top: isBottomLayout ? 'auto' : 60,
        width: 'auto',
        maxWidth: isBottomLayout ? 'none' : 200,
        minWidth: isBottomLayout ? 0 : 100,
        flex: isBottomLayout ? 'none' : isTabletOrBelow ? '0 0 130px' : '1 1 0',
      }}
    >
      {isBottomLayout ? (
        <>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: 'center',
              width: 'auto',
              minWidth: 'fit-content',
            }}
          >
            {showCancelButton && (
              <Button
                color="secondary"
                disabled={isPending}
                onClick={handleCancelClick}
                variant="outlined"
                sx={{ width: 150 }}
              >
                {BUTTON_TEXT.cancel}
              </Button>
            )}
            {showBackButton && (
              <Button
                color="secondary"
                disabled={isPending}
                onClick={handleBackClick}
                variant="outlined"
                sx={{ width: 150 }}
              >
                {backButtonlabel}
              </Button>
            )}
          </Stack>

          {showPrimaryButton && (
            <Button
              color="secondary"
              disabled={!isDirty || isSubmitDisabled}
              disableElevation
              loading={isPending}
              onClick={handleSubmit}
              variant="contained"
              sx={{ width: 150 }}
            >
              {primaryButtonLabel}
            </Button>
          )}
        </>
      ) : (
        <>
          {showPrimaryButton && (
            <Button
              color="secondary"
              disabled={!isDirty || isSubmitDisabled}
              disableElevation
              fullWidth
              loading={isPending}
              onClick={handleSubmit}
              variant="contained"
            >
              {primaryButtonLabel}
            </Button>
          )}
          {showCancelButton && (
            <Button
              color="info"
              disabled={isPending}
              fullWidth
              onClick={handleCancelClick}
              variant="outlined"
            >
              {BUTTON_TEXT.cancel}
            </Button>
          )}
          {showBackButton && (
            <Button
              color="info"
              disabled={isPending}
              fullWidth
              onClick={handleBackClick}
              variant="outlined"
            >
              {backButtonlabel}
            </Button>
          )}
        </>
      )}
      {showExtraButtons && (
        <>
          <Divider sx={{ width: '100%' }} />
          {extraButtons.map((button, index) => {
            const { color, isDisabled, onClick, tooltipMessage, variant } =
              button;

            return (
              <TooltipWrapper key={index} tooltipMessage={tooltipMessage}>
                <Button
                  color={color || 'info'}
                  disableElevation
                  disabled={isPending || isDisabled}
                  fullWidth
                  onClick={onClick}
                  variant={variant ?? 'outlined'}
                >
                  {button.label}
                </Button>
              </TooltipWrapper>
            );
          })}
        </>
      )}
    </Stack>
  );
};

export default FormButtonColumn;
